import { occasionsWithDefaultDates } from '../shared/config'
import * as Luxon from 'luxon'
import { isUndefined, filter, values, keys } from 'lodash'
// type OccasionStringType = 'birthday' | 'anniversary' | 'valentines' | 'other'

export const MIN_FULFILLMENT_DAYS = 6

export const OCCASIONS_NEEDING_DATE = ['birthday', 'anniversary']

// ----------------
// Relation
// ----------------

interface RelationType {
  value: string,
  label: string,
  cards: string[],
  possessiveAdjective: string,
  defaultPronoun: string,
  objectivePronoun: string,
}

export const relationTypesObject: { [index: string]: RelationType } = {
  wife: {
    value: 'wife',
    label: 'wife',
    cards: ['birthday', 'anniversary', 'valentines', 'mothers day', 'mothersday', 'just because'],
    possessiveAdjective: 'her',
    defaultPronoun: 'she',
    objectivePronoun: 'her',
  },
  girlfriend: {
    value: 'girlfriend',
    label: 'girlfriend',
    cards: ['birthday', 'anniversary', 'valentines', 'mothers day', 'mothersday', 'just because'],
    possessiveAdjective: 'her',
    defaultPronoun: 'she',
    objectivePronoun: 'her',
  },
  husband: {
    value: 'husband',
    label: 'husband',
    cards: ['birthday', 'anniversary', 'valentines', 'fathers day', 'fathersday', 'just because'],
    possessiveAdjective: 'his',
    defaultPronoun: 'he',
    objectivePronoun: 'him',
  },
  boyfriend: {
    value: 'boyfriend',
    label: 'boyfriend',
    cards: ['birthday', 'anniversary', 'valentines', 'fathers day', 'fathersday', 'just because'],
    possessiveAdjective: 'his',
    defaultPronoun: 'he',
    objectivePronoun: 'him',
  },
  partner: {
    value: 'partner',
    label: 'partner',
    cards: ['birthday', 'anniversary', 'valentines', 'fathers day', 'fathersday', 'mothers day', 'mothersday', 'just because'],
    possessiveAdjective: 'their',
    defaultPronoun: 'they',
    objectivePronoun: 'them',
  },
  son: {
    value: 'son',
    label: 'son',
    cards: ['birthday', 'fathers day', 'fathersday', 'just because'],
    possessiveAdjective: 'his',
    defaultPronoun: 'he',
    objectivePronoun: 'him',
  },
  daughter: {
    value: 'daughter',
    label: 'daughter',
    cards: ['birthday', 'mothers day', 'mothersday', 'just because'],
    possessiveAdjective: 'her',
    defaultPronoun: 'she',
    objectivePronoun: 'her',
  },
  mother: {
    value: 'mother',
    label: 'mother',
    cards: ['birthday', 'mothers day', 'mothersday', 'just because'],
    possessiveAdjective: 'her',
    defaultPronoun: 'she',
    objectivePronoun: 'her',
  },
  father: {
    value: 'father',
    label: 'father',
    cards: ['birthday', 'fathers day', 'fathersday', 'just because'],
    possessiveAdjective: 'his',
    defaultPronoun: 'he',
    objectivePronoun: 'him',
  },
  brother: {
    value: 'brother',
    label: 'brother',
    cards: ['birthday', 'just because'],
    possessiveAdjective: 'his',
    defaultPronoun: 'he',
    objectivePronoun: 'him',
  },
  sister: {
    value: 'sister',
    label: 'sister',
    cards: ['birthday', 'just because'],
    possessiveAdjective: 'her',
    defaultPronoun: 'she',
    objectivePronoun: 'her',
  },
  friend: {
    value: 'friend',
    label: 'friend',
    cards: ['birthday', 'just because'],
    possessiveAdjective: 'their',
    defaultPronoun: 'they',
    objectivePronoun: 'their',
  },
  // kidsFriend: {
  //   value: 'kidsFriend',
  //   label: 'kid’s friend',
  //   cards: ['birthday', 'valentines', 'just because'],
  //   possessiveAdjective: 'their',
  //   defaultPronoun: 'they',
  //   objectivePronoun: 'their',
  // },
  other: {
    value: 'other',
    label: 'other',
    cards: ['birthday', 'anniversary', 'valentines', 'just because'],
    possessiveAdjective: 'their',
    defaultPronoun: 'they',
    objectivePronoun: 'their',
  }
}

export const relationTypes = values(relationTypesObject)
export const listOfRelationTypes = relationTypes.map(relation => relation.label)

export const getRelationTypesForOccasion = (occasionTitle: string) => values(filter(relationTypesObject, (relation: RelationType) => relation.cards.includes(occasionTitle)))
export const getListOfRelationTypesForOccasion = (occasionTitle: string) => getRelationTypesForOccasion(occasionTitle).map(relation => relation.label)

const objectivePronounsByOccasion: any = {
  anniversary: 'our',
  'fathers day': '',
  'valentines': '',
  'mothers day': '',
}

export const getPossessiveAdjectiveForRelation = (relation:string) : string => relationTypesObject?.[relation]?.possessiveAdjective

export const getPossessiveAdjectiveForRelationAndOccasion = (relation:string, occasion:string) : string => {
  const occasionPossessive = objectivePronounsByOccasion?.[occasion]
  return isUndefined(occasionPossessive)
    ? relationTypesObject?.[relation]?.possessiveAdjective
    : occasionPossessive
}

export const getPronounForRelation = (relation:string) : string => relationTypesObject[relation].defaultPronoun

export const getObjectivePronounForRelation = (relation:string) : string => relationTypesObject[relation].objectivePronoun

interface relationSingularTypes {
  [index: string]: boolean,
  wife: boolean,
  girlfriend: boolean,
  husband: boolean,
  boyfriend: boolean,
  partner: boolean,
  son: boolean,
  daughter: boolean,
  mother: boolean,
  father: boolean,
  friend: boolean,
  other: boolean,
}

const isRelationSingular: relationSingularTypes = {
  wife: true,
  girlfriend: true,
  husband: true,
  boyfriend: true,
  partner: false,
  son: true,
  daughter: true,
  mother: true,
  father: true,
  friend: false,
  other: false,
}

export const getConjugatedVerbForRelation = (relation:string, verbSingular:string, verbPlural:string) : string => isRelationSingular[relation] ? verbSingular : verbPlural

// ----------------
// CARD TYPE CONFIG
// ----------------

type occasionMonthAndDayShape = {
  occasionMonth: string,
  occasionDay: string,
}

const occasionMonthAndDay: { [key: string]: occasionMonthAndDayShape } = {
  'valentines': { occasionMonth: '02', occasionDay: '15' },
  'fathers day': { occasionMonth: '06', occasionDay: '20' },
  'mothers day': { occasionMonth: '05', occasionDay: '08' },
}

export const getNextDate = (occasionName: string) => {
  const today = new Date()
  const thisYear = today.getFullYear()
  if (!occasionMonthAndDay?.[occasionName]) {
    return undefined
  }
  const { occasionMonth, occasionDay } = occasionMonthAndDay[occasionName]
  const occasionDate = new Date(`${thisYear}-${occasionMonth}-${occasionDay}`)
  const soonestFulfillmentDate = Luxon.DateTime.local().plus({ days: MIN_FULFILLMENT_DAYS })
  const nextOccasionDate = (occasionDate <= soonestFulfillmentDate.toJSDate())
    ? new Date(`${thisYear + 1}-${occasionMonth}-${occasionDay}`)
    : occasionDate
  return nextOccasionDate
}

export type cardCategoryDetailsShape = {
  value: string,
  label: string,
  inlineLabel: string,
  date?: Date,
  personLabel?: string,
  emojiLabel: string,
  indefiniteArticle: string,
}

export const cardCategories: { [key: string]: cardCategoryDetailsShape } = {
  birthday: {
    value: 'birthday',
    label: 'birthday',
    inlineLabel: 'birthday',
    personLabel: 'someone special',
    emojiLabel: '🎂',
    indefiniteArticle: 'a',
  },
  anniversary: {
    value: 'anniversary',
    label: 'anniversary',
    inlineLabel: 'anniversary',
    personLabel: 'that special someone',
    emojiLabel: '💍',
    indefiniteArticle: 'an',
  },
  valentines: {
    value: 'valentines',
    label: 'Valentine’s Day',
    inlineLabel: 'Valentine’s Day',
    date: getNextDate('valentines'),
    emojiLabel: '♥️',
    indefiniteArticle: 'a',
  },
  // 'fathers day': {
  //   value: 'fathers day',
  //   label: 'Father’s Day',
  //   inlineLabel: 'Father’s Day',
  //   date: getNextDate('fathers day'),
  //   personLabel: 'the special dad in your life',
  // },
  fathersday: {
    value: 'fathers day',
    label: 'Father’s Day',
    inlineLabel: 'Father’s Day',
    date: getNextDate('fathers day'),
    personLabel: 'the special dad in your life',
    emojiLabel: '👨&zwj;👧&zwj;👦',
    indefiniteArticle: 'a',
  },
  // 'mothers day': {
  //   value: 'mothers day',
  //   label: 'Mother’s Day',
  //   inlineLabel: 'Mother’s Day',
  //   date: getNextDate('mothers day'),
  //   personLabel: 'the special mom in your life',
  // },
  mothersday: {
    value: 'mothers day',
    label: 'Mother’s Day',
    inlineLabel: 'Mother’s Day',
    date: getNextDate('mothers day'),
    personLabel: 'the special mom in your life',
    emojiLabel: '👩&zwj;👧&zwj;👦',
    indefiniteArticle: 'a',
  },
  justbecause: {
    value: 'just because',
    label: 'Just Because',
    inlineLabel: 'Just Because',
    personLabel: 'that special someone',
    emojiLabel: '💌',
    indefiniteArticle: 'a',
  },
}

export const DEFAULT_OCCASION_LABEL = 'birthday, anniversary, and Valentine’s'
export const getLabelForOccasionCategory = (categoryTitle:string) =>
  cardCategories?.[categoryTitle.replace(' ', '')]?.label || ''

export const getLabelForCardCategories = (categoryTitles: string[]) => {
  const formattedTitles = categoryTitles
    .map(categoryTitle =>
      cardCategories?.[categoryTitle.replace(' ', '')]?.label || ''
    )
    .filter(categoryTitle =>
      !!categoryTitle
    )
    .join('/')

  return formattedTitles
}

export const DEFAULT_PERSON_LABEL = 'someone you love'
export const getPersonLabelForCardCategory = (categoryTitle:string) => cardCategories?.[categoryTitle]?.personLabel || DEFAULT_PERSON_LABEL

export const getCardCategoryDetailsForOccasionKey = (occasionKey: string) =>
  cardCategories?.[occasionKey.replace(' ', '')]

export const cardTypes = values(cardCategories)
export const cardTypeKeys = keys(cardCategories)

export const getCardTypesForRelation = (relationKey: string | undefined) => {
  if (!relationKey) return cardTypes
  const relationData = relationTypesObject[relationKey]
  return cardTypes.filter(card => relationData?.cards?.includes(card.value))
}

export const noDefaultDateForOccasion = (occasionTitle:string) => !occasionsWithDefaultDates.includes(occasionTitle)
